import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, Grid, IconButton, Modal, Stack, Tooltip, Typography } from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import registerUserIcon from '../../assets/SVG/register-user.svg';
import rescheduleIcon from '../../assets/SVG/Reschedule.svg';
import cancelIcon from '../../assets/SVG/Cancel.svg';
import checkInIcon from '../../assets/SVG/Check in.svg';
import VideoConsultation from '../../assets/SVG/video_consultation.svg';
import cancelAlertIcon from '../../assets/SVG/Cancel (1).svg';
import checkOutIcon from '../../assets/SVG/tag-cross.svg';
import { Appointment, AppointmentStatus, VidaoConsultationData } from '../../types';
import { getAppointmentById, updateAppointmentStatus } from '../../services/apiService';
import { convertISOToTime, convertToLocalDateTime } from '../../services/time';

import { useLoader } from '../loaderProvider/loader';
import Rescheduled from '../appointmentRescheduleModal';
import Cancel from '../appointmentCancelModal';
import { useNavigate } from 'react-router-dom';
import { checkDatePassed, convertUnderscoreToTitle, toTitleCase } from '../../utils/commonUtils';
import { getButtonStates } from '../../utils/permissionService';
import AppointmentModalSkeleton from '../../utils/AppointmentModalSkeleton';
import RemarkPopover from '../Billing/common/RemarkPopover';
import { formatDate } from '../../utils/dateUtils';

type ICustomModal = {
  id: number;
  isOpen: boolean;
  onClose: (value: boolean) => void;
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'auto',
  height: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: '20px'
};


const CustomModal: React.FC<ICustomModal> = ({ id, isOpen, onClose }) => {

  const { t: tAppointmentForm } = useTranslation('appointment_form');
  const { t: tCalendar } = useTranslation('calendar');
  
  // Function to get localized department names
  const getDepartmentDisplayName = (departmentName: string) => {
    const nameMap: { [key: string]: string } = {
      'Admin': tCalendar('admin'),
      'Pathology': tCalendar('pathology'),
      'Semen Collection': tCalendar('semenCollection'),
      'Consultation': tCalendar('consultation'),
      'Ultrasound': tCalendar('ultrasound'),
      'Surgery': tCalendar('surgery')
    };
    return nameMap[departmentName] || departmentName;
  };
  
  const [appointment, setAppointment] = useState<Appointment | undefined>();
  const { showLoader, hideLoader } = useLoader();
  const [rescheduled, setRescheduled] = useState<boolean>(false);
  const [cancel, setCancel] = useState<boolean>(false);
  const [statusUpdated, setStatusUpdated] = useState(false);
  const [consultationUrl, setConsultationUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  // Remark popover state
  const [remarkAnchorEl, setRemarkAnchorEl] = useState<null | HTMLElement>(null);
  const [remarkText, setRemarkText] = useState<string>('');

  const handleOpenRemark = (event: React.MouseEvent<HTMLElement>, text: string) => {
    event.stopPropagation();
    setRemarkAnchorEl(event.currentTarget);
    setRemarkText(text);
  };

  const handleCloseRemark = () => {
    setRemarkAnchorEl(null);
    setRemarkText('');
  };
  
  // Get button states only when appointment data is available
  const { canEdit } = appointment?.department__name 
    ? getButtonStates('Admin', 'Calendar', appointment.department__name)
    : { canEdit: false };
  
  useEffect(() => {
    if (!isOpen) return; // Don't fetch if modal is not open
    
    showLoader();
    if (id) {
      getAppointmentById(id)
        .then((appointment: Appointment) => {
          setAppointment(appointment);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('err', error);
        })
        .finally(() => {
          hideLoader();
        });
    } else {
      hideLoader();
    }
  }, [id, hideLoader, showLoader, statusUpdated, isOpen]);

  // Cleanup effect to handle modal close
  useEffect(() => {
    if (!isOpen) {
      setAppointment(undefined);
      setRescheduled(false);
      setCancel(false);
      setStatusUpdated(false);
      hideLoader();
    }
  }, [isOpen, hideLoader]);

  const renderDetail = (label: string, value: string | undefined, opts?: { lowercase?: boolean }) => (  
    <Grid item xs={12} sm={6} md={4} justifyContent='space-between'>
      <Typography fontFamily='Nunito' fontWeight={400} fontSize='14px' color='#9E9E9E'>
        {label}
      </Typography>
      <Typography color='#232323' fontFamily='Nunito' textTransform={opts?.lowercase ? 'lowercase': 'capitalize'} fontWeight={500} fontSize='16px'>
        {value ?? tAppointmentForm('na')}
      </Typography>
    </Grid>
  );
  const chipValue = appointment?.status;
  const getStatusLabel = (status?: AppointmentStatus) => {
    if (!status) return '';
    const map: Record<AppointmentStatus, string> = {
      [AppointmentStatus.SCHEDULED]: 'scheduled',
      [AppointmentStatus.CHECKED_IN]: 'checkedIn',
      [AppointmentStatus.CHECKED_OUT]: 'checkedOut',
      [AppointmentStatus.CANCELLED]: 'cancelled',
      [AppointmentStatus.COMPLETED]: 'completed',
      [AppointmentStatus.UNATTENDED]: 'unattended',
    } as const;
    const key = map[status];
    return key ? tCalendar(key) : '';
  };
  const chipTextColor = chipValue === AppointmentStatus.COMPLETED ? '#47B35F' : chipValue === AppointmentStatus.UNATTENDED ? '#F7BD8E' : chipValue === AppointmentStatus.CHECKED_IN ? '#3A80E2' : 'gray';
  const chipColor = chipValue === AppointmentStatus.COMPLETED ? 'success' : chipValue === AppointmentStatus.UNATTENDED ? 'warning' : chipValue === AppointmentStatus.CHECKED_IN ? 'info' : 'primary';
  const chipBgColor = chipValue === AppointmentStatus.COMPLETED ? '#F4FFF6' : chipValue === AppointmentStatus.UNATTENDED ? '#FFF8F3' : chipValue === AppointmentStatus.CHECKED_IN ? '#F5F9FF' : '#ffffff';

  const checkInTime = appointment?.checked_in_time? convertToLocalDateTime(appointment?.checked_in_time): undefined;
  const checkOutTime = appointment?.checked_out_time? convertToLocalDateTime(appointment?.checked_out_time): undefined;

  const handleModalClose = () => {
    hideLoader(); // Ensure loader is hidden when closing
    setAppointment(undefined);
    onClose(false);
    setRescheduled(false);
    setCancel(false);
  };

  const updateStatus = (status: string) => {
    const data = appointment;

    updateAppointmentStatus(data?.id || 0, status)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((response) => {
        setStatusUpdated(!statusUpdated);
      })
      .catch((error) => {
      // eslint-disable-next-line no-console
        console.error('Error updating status:', error);
      });
  };


  const isNewPatient = () => {
    if (appointment?.patient?.additional_info?.ethnicity) {
      return false;
    }
    return true;
  };

  
  // const isPast = checkDatePassed(appointment?.appointment_date || '');
  // const isToday = appointment?.appointment_date === new Date().toISOString().split('T')[0];
  const isPast = checkDatePassed(appointment?.start_datetime || '');
  const isToday = appointment?.start_datetime
    ? new Date(appointment.start_datetime).toISOString().split('T')[0] === new Date().toISOString().split('T')[0]
    : false;
 
  // Don't render anything if modal is not open
  if (!isOpen) {
    return null;
  }

  const startConsultation = (consultationData: VidaoConsultationData) => {
    const url = `https://remote-epson-sierra-wood.trycloudflare.com/room/${consultationData.room_id}?meeting_id=${consultationData.meeting_id}&role=doctor`;
    // window.open(url, '_blank');
    setConsultationUrl(url);
  };

  const closeConsultation = () => {
    setConsultationUrl(null);
  };

  if (appointment){
    return (
      <>
        <Modal open={isOpen} onClose={handleModalClose}>
          <Box sx={style}>
  
            {
              (!rescheduled && !cancel) ?
                <Stack direction='row' justifyContent='space-between'>
                  <Stack direction='row' gap={2} alignItems='center'>
                    <Typography color='#232323' fontSize='20px' fontWeight={700}>{tAppointmentForm('patientInformationTitle')}</Typography>
                    {appointment?.unattended_to_scheduled && (
                      <Chip 
                        label={`${convertUnderscoreToTitle(tCalendar('unattended'))} → ${convertUnderscoreToTitle(tCalendar('rescheduled'))}`}
                        size='small'
                        sx={{ 
                          backgroundColor: '#FFF3E0', 
                          color: '#666', 
                          fontWeight: 600,
                          fontSize: '12px'
                        }} 
                      />
                    )}
                  </Stack>
                  <Stack direction='row' gap={3} alignItems="center">
                    <Chip label={getStatusLabel(chipValue)} variant='outlined' sx={{ color: chipTextColor, fontWeight: 700, background: chipBgColor }} color={chipColor} />
                    <Tooltip title="Close" arrow  >
                      <IconButton
                        onClick={handleModalClose}
                        size="small"
                        sx={{
                          color: '#666',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            color: '#333',
                          },
                          '&:active': {
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                          },
                          transition: 'all 0.2s ease-in-out',
                        }}
                        aria-label="Close modal"
                      >
                        <CloseOutlinedIcon fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack> :  
  
              // Rescheduled / Cancel Heading
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div>
                      <img src={cancelAlertIcon} alt='cancel-icon' />
                    </div>
  
                    <div>
                      <Typography fontFamily='Montserrat' fontWeight={600} fontSize='20px' color='#232323'>
                        {rescheduled ? tAppointmentForm('rescheduleAppointmentTitle') : tAppointmentForm('cancelAppointmentTitle')}
                      </Typography>
                      <Typography fontFamily='Nunito' fontWeight={400} fontSize='14px' color='#404653'>
                        {rescheduled ? tAppointmentForm('rescheduleAppointmentConfirm') : tAppointmentForm('cancelAppointmentConfirm')}
                      </Typography>
                    </div>
                  </div>
                  <Tooltip title="Close" arrow  >
                    <IconButton
                      onClick={handleModalClose}
                      size="small"
                      sx={{
                        color: '#666',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          color: '#333',
                        },
                        '&:active': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      aria-label="Close modal"
                    >
                      <CloseOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </Tooltip>
                </Stack>
            }
  
  
            {
              rescheduled ?
                <Rescheduled data={appointment} onClose={handleModalClose} open={isOpen}/> : 
                cancel ? 
                  <Cancel data={appointment} onClose={handleModalClose} /> : 
                  <>
                    <Grid container spacing={3} mt={2}>
                      {renderDetail(tAppointmentForm('firstNameLabel'), appointment?.patient?.first_name)}
                      {renderDetail(tAppointmentForm('lastNameLabel'), appointment?.patient?.last_name)}
                      {renderDetail(tAppointmentForm('sexAssignedAtBirthLabel'), toTitleCase(appointment?.patient?.gender) ?? appointment?.patient?.gender)}
                      {appointment?.patient?.mobile_number_country_code
                        ? renderDetail(tAppointmentForm('mobileLabel'), `${appointment.patient.mobile_number_country_code.startsWith('+') ? '' : '+'}${appointment.patient.mobile_number_country_code}-${appointment.patient.mobile_number}`)
                        : renderDetail(tAppointmentForm('mobileLabel'), `${appointment?.patient?.mobile_number}`)}
                      {renderDetail(tAppointmentForm('dobLabel'), formatDate(appointment?.patient?.date_of_birth || ''))}
                      {renderDetail(tAppointmentForm('emailLabel'), appointment?.patient?.alternate_email || ('-'), { lowercase: true })}
                      {renderDetail(tAppointmentForm('departmentLabel'), getDepartmentDisplayName(appointment?.department__name_label || appointment?.department__name || ''))}
                      {(() => {
                        const label = appointment?.department__name === 'Admin' ? tAppointmentForm('personnelLabel') : 
                          appointment?.department__name === 'Pathology' ? tAppointmentForm('pathologistLabel') : tAppointmentForm('doctorNameLabel');
                        const name = `${appointment?.user?.first_name ?? ''} ${appointment?.user?.last_name ?? ''}`.trim();
                        // If name is empty, show NA
                        return renderDetail(label, name && name !== `${tAppointmentForm('doctorPrefix')}` ? name : ('-'));
                      })()}

                      {renderDetail(tAppointmentForm('appointmentReasonLabel'), appointment?.appointment_reason__reason__label ?? ('-'))}
                      {renderDetail(tAppointmentForm('dateLabel'), formatDate(appointment?.appointment_date || ''))}
                      {renderDetail(tAppointmentForm('timeLabel'), `${convertISOToTime(appointment?.start_datetime || '', true)} - ${convertISOToTime(appointment?.end_datetime || '', true)}`, { lowercase: true })}
                      <Grid item xs={12} sm={6} md={4} justifyContent='space-between'>
                        <Typography fontFamily='Nunito' fontWeight={400} fontSize='14px' color='#9E9E9E'>
                          {tAppointmentForm('remarkLabel')}
                        </Typography>
                        {appointment?.remark && appointment.remark.length > 20 ? (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Tooltip title={tAppointmentForm('remarkLabel')} arrow>
                              <IconButton
                                size="small"
                                sx={{ p: 0, color: '#5A8AEA', flexShrink: 0 }}
                                aria-label={tAppointmentForm('remarkLabel')}
                                onClick={(e: React.MouseEvent<HTMLElement>) => handleOpenRemark(e, appointment.remark!)}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography color='#232323' fontFamily='Nunito' fontWeight={500} fontSize='16px'>
                              {appointment.remark.slice(0, 20)}...
                            </Typography>
                          </Box>
                        ) : (
                          <Typography color='#232323' fontFamily='Nunito' fontWeight={500} fontSize='16px'>
                            {appointment?.remark ?? tAppointmentForm('na')}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
  
                 
                    { (checkInTime || !isPast) && <Divider style={{ height: '1px', background: '#595959', marginTop: 20, marginBottom: 20 }} />}
                    { (isNewPatient()) && chipValue !== AppointmentStatus.CANCELLED && (
                      <Typography color='warning'>
                        {tAppointmentForm('notePrefix')} {tAppointmentForm('registrationRequiredNote')}
                      </Typography>
                    )}
                    { chipValue === AppointmentStatus.CANCELLED && <Typography color='error'>{tAppointmentForm('appointmentIsCancelled')}</Typography>}
                    {(checkInTime && (appointment?.status === AppointmentStatus.CHECKED_IN || appointment?.status === AppointmentStatus.CHECKED_OUT)) && <Stack direction='row' gap={4}>
                      {checkInTime && <div style={{ display: 'flex', gap: 5 }}>
                        <Typography color='#9E9E9E' fontSize='14px' fontWeight={400}>{tAppointmentForm('checkInAction')} :</Typography>
                        <Typography color='#232323' fontSize='14px' fontWeight={600}>{convertISOToTime(checkInTime.toString(), true)}</Typography>
                      </div>}
  
                      {checkOutTime && (appointment?.status === AppointmentStatus.CHECKED_OUT) && <div style={{ display: 'flex', gap: 5 }}>
                        <Typography color='#9E9E9E' fontSize='14px' fontWeight={400}>{tAppointmentForm('checkOutAction')} :</Typography>
                        <Typography color='#232323' fontSize='14px' fontWeight={600}>{convertISOToTime(checkOutTime.toString(), true)}</Typography>
                      </div>}
                    </Stack>}
                    {/* Checking if the appointment date has passed or not */}
                    {canEdit ? (
                      !isPast && (
                        <Stack direction='row' justifyContent='space-between' alignItems='center' mt={appointment?.status === AppointmentStatus.CHECKED_OUT ? 0 : 3}>
                          <Stack direction='row' gap={6}>
                            {((!checkInTime && isNewPatient() && !appointment?.patient?.additional_info?.ethnicity?.length) && 
                          <div style={{ display: 'flex', gap: 5, cursor: 'pointer', alignItems: 'center' }} 
                            onClick={() => navigate('/admin/registration/patient-partner-registration', { state: appointment?.patient })}>
                            <img src={registerUserIcon} width={22} alt='register-icon' />
                            <Typography>{tAppointmentForm('registerAction')}</Typography>
                          </div>
                            )}
                            {
                              ((appointment?.status === AppointmentStatus.SCHEDULED || appointment?.status === AppointmentStatus.UNATTENDED) && chipValue !== AppointmentStatus.CANCELLED && appointment?.appointment_mode !== 'ONLINE' &&
                              isToday && 
                              <div style={{ display: 'flex', gap: 5, cursor: appointment?.patient?.additional_info?.ethnicity?.length ? 'pointer' : 'not-drop' }}>
                                <div
                                  onClick={() => appointment?.patient?.additional_info?.ethnicity?.length && updateStatus(AppointmentStatus.CHECKED_IN)}
                                  style={{ display: 'flex', alignItems: 'center', gap: '5px', 
                                    pointerEvents: appointment?.patient?.additional_info?.ethnicity?.length ? 'auto' : 'none'  }}
                                >
                                  <img src={checkInIcon} alt='checkIn-icon' />
                                  <Typography color={appointment?.patient?.additional_info?.ethnicity?.length ? '#000' : '#c1c1c1'}>{tAppointmentForm('checkInAction')}</Typography>
                                </div>
                              </div>
                              )
                            }
                            { chipValue !== AppointmentStatus.CHECKED_OUT && chipValue !== AppointmentStatus.CHECKED_IN && (
                              <>
                                <div style={{ display: 'flex', gap: 5, cursor: 'pointer', alignItems: 'center' }} onClick={() => setRescheduled(true)}>
                                  <img src={rescheduleIcon} width={22} alt='register-icon' />
                                  <Typography>{tAppointmentForm('rescheduleAction')}</Typography>
                                </div>
    
                                {chipValue !== AppointmentStatus.CANCELLED  && (
                                  <div
                                    style={{ display: 'flex', gap: 5, cursor: 'pointer', alignItems: 'center' }}
                                    onClick={() => setCancel(true)}
                                  >
                                    <img src={cancelIcon} width={22} alt='cancel-user' />
                                    <Typography>{tAppointmentForm('cancelAction')}</Typography>
                                  </div>
                                )}
                              </>
                            )}
                            { (chipValue === AppointmentStatus.CHECKED_IN && isToday ) &&
                            <div style={{ display: 'flex', gap: 5, cursor: 'pointer', alignItems: 'center' }} onClick={() => updateStatus('CHECKED_OUT')}>
                              <img src={checkOutIcon} alt='checkOut-icon' />
                              <Typography>{tAppointmentForm('checkOutAction')}</Typography>
                            </div>
                            }
                          </Stack>

                          {/* Start Consultation Button - disabled when check-in not available */}
                          {
                            appointment?.appointment_mode === 'ONLINE' && (
                              <Button
                                variant="contained" 
                                sx={{ minWidth: 160, borderRadius: 2 }}
                                disabled={!(appointment?.status === AppointmentStatus.SCHEDULED && 
                              chipValue !== AppointmentStatus.CANCELLED && appointment?.vidao_consultation_data  )}
                                onClick={() => {
                                  if (appointment.vidao_consultation_data) {
                                    startConsultation(appointment.vidao_consultation_data);
                                  }
                                }}
                              >
                                <img src={VideoConsultation} alt='video-consultation' width={20}  style={{ marginRight: 8 }}/>

                                {tAppointmentForm('startConsultation')}
                              </Button>
                            )
                          }
                    
                        </Stack>
                      )
                    ) : (
                      <Typography color='error' sx={{ mt: 2, textAlign: 'center', fontWeight: 600 }}>
                        {tAppointmentForm('noAccessToModifyAppointment')}
                      </Typography>
                    )}
                  </> 
            }
  
          </Box>
        </Modal>

        {/* Consultation Iframe Modal */}
        <Modal 
          open={!!consultationUrl} 
          onClose={closeConsultation}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box sx={{
            width: '90vw',
            height: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <IconButton
              onClick={closeConsultation}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
              }}
            >
              <CloseOutlinedIcon />
            </IconButton>
            {consultationUrl && (
              <iframe
                src={consultationUrl}
                title="Video Consultation"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allow="camera *; microphone *; fullscreen *; display-capture *"
                allowFullScreen
              />
            )}
          </Box>
        </Modal>
        <RemarkPopover
          anchorEl={remarkAnchorEl}
          content={remarkText}
          open={Boolean(remarkAnchorEl)}
          onClose={handleCloseRemark}
          title={tAppointmentForm('remarkLabel')}
        />
      </>
    );
  } else {
    // Show skeleton loader that matches the appointment modal structure
    return (
      <Modal open={isOpen} onClose={handleModalClose}>
        <Box sx={{
          ...style,
          width: '50%',
          minHeight: '400px'
        }}>
          <AppointmentModalSkeleton 
            rows={4} 
            columns={3} 
            showHeader={true}
            showDivider={true}
            showActions={true}
            showAdditionalInfo={true}
          />
        </Box>
      </Modal>
    );
  }
};

export default CustomModal;